import { useRef, useState } from "react";
import { DragDropProvider } from "@dnd-kit/react";
import { Check, CornerDownRight } from "lucide-react";
import { SubtaskTree } from "./SubtaskTree";
import { SubtaskDates } from "./SubtaskDates";
import { SubtaskMenu } from "./SubtaskMenu";
import { pointer, windowAutoScrollPlugins } from "./TaskGroupViews";
import { NestDepoContext, NestDropContext, useNestDnd } from "../lib/nestDrop";
import { updateSubById, computeSubNumbers, findSubById, removeSubById } from "../lib/subtaskTree";

// AŞAMA 9 (Adım 2) + AŞAMA 10 — yüzen pencerede gösterilen alt görev içeriği.
// Üst satır (alt görevin kendisi) + iç görevler. İç görevler kendi kardeş
// grubunda sıralanabilir; pencere SINIRLARININ DIŞINA bırakılırsa kendi
// yüzen penceresine çıkar (her seviyede, özyinelemeli).
export const DetachedSubtaskContent = ({
  task,
  sub,
  onSetSubtasks,
  onSubtaskDrag,
  highlight = "",
  detachedSubIds = null,
  onDetachSubtask,
  onDockSubtask,
  onDropIntoTask,
  invalidDropIdsFor,
  dragCountOf,
  kapsam = "altpencere",
}) => {
  const { nest, depo, baslat, uzerinde, bitir } = useNestDnd({
    invalidDropIdsFor, dragCountOf, onDropIntoTask, kapsam,
  });
  const all = Array.isArray(task.subtasks) ? task.subtasks : [];
  const numbers = computeSubNumbers(all);
  const isDone = sub.done || sub.status === "done";
  const kokRef = useRef(null);
  const [menu, setMenu] = useState(null); // { id, x, y }
  const setDone = (done) => onSetSubtasks(updateSubById(all, sub.id, { done, status: done ? "done" : "pending" }), {});
  const setDates = (id, patch) => onSetSubtasks(updateSubById(all, id, patch), {});
  const hasChildren = Array.isArray(sub.children) && sub.children.length > 0;

  // Pencere sınırlarının dışına bırakıldı mı?
  const disaridaMi = () => {
    const win = kokRef.current?.closest("[data-detached-subwindow]");
    if (!win || pointer.x == null) return false;
    const r = win.getBoundingClientRect();
    return pointer.x < r.left || pointer.x > r.right || pointer.y < r.top || pointer.y > r.bottom;
  };

  const menuSub = menu ? findSubById(all, menu.id) : null;
  const ctx = {
    taskId: task.id,
    numbers,
    selectMode: false,
    selectedIds: new Set(),
    highlight,
    onToggle: (id, done) => onSetSubtasks(updateSubById(all, id, { done, status: done ? "done" : "pending" }), {}),
    onSetSubDates: setDates,
    onOpenMenu: (id, x, y) => setMenu({ id, x, y }),
    onSelectToggle: () => {},
    addChildParentId: null,
    newChildText: "",
    setNewChildText: () => {},
    commitAddChild: () => {},
    cancelAddChild: () => {},
    detachedSubIds: detachedSubIds || new Set(),
    onDockSubtask: (id) => onDockSubtask?.(id),
  };

  return (
    <div className="space-y-2" ref={kokRef} data-testid={`detached-subtask-content-${sub.id}`}>
      <div className="flex items-start gap-2 rounded border border-violet-400/30 bg-violet-500/5 px-2 py-1.5">
        <button
          onClick={() => setDone(!isDone)}
          data-testid={`detached-subtask-check-${sub.id}`}
          title={isDone ? "Tamamlandı işaretini kaldır" : "Tamamlandı işaretle"}
          className={`mt-0.5 h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${
            isDone ? "border-emerald-400 bg-emerald-500/40" : "border-violet-400/50 hover:bg-violet-500/15"
          }`}
        >
          {isDone && <Check className="h-2.5 w-2.5 text-white" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-mono ${isDone ? "text-sertex-textMuted line-through" : "text-sertex-text"}`}>
            {numbers[sub.id] != null && <span className="mr-1 tabular-nums text-violet-300">{isDone ? "✓" : `${numbers[sub.id]}.`}</span>}
            {sub.text}
          </div>
          <div className="text-[10px] font-mono text-sertex-textMuted truncate">GÖREV · {task.title}</div>
          <SubtaskDates sub={sub} taskId={task.id} onSetDates={setDates} />
        </div>
      </div>
      {hasChildren && (
        <DragDropProvider
          plugins={windowAutoScrollPlugins}
          onDragStart={baslat}
          onDragOver={(event, manager) => {
            if (uzerinde(event, manager)) return;
            onSubtaskDrag(event, true);
          }}
          onDragEnd={(event, manager) => {
            const hedef = bitir(event, manager);
            if (hedef) {
              onSubtaskDrag(event, false, true);
              onDropIntoTask(hedef, {
                id: event?.operation?.source?.id,
                data: event?.operation?.source?.data || {},
              });
              return;
            }
            // AŞAMA 10 (kural 2) — pencere dışına bırakılan iç görev kendi
            // penceresine çıkar; sıralama önizlemesi geri alınır.
            if (!event?.canceled && disaridaMi()) {
              onSubtaskDrag(event, false, true);
              onDetachSubtask?.(event.operation.source.id, { x: pointer.x, y: pointer.y });
              return;
            }
            onSubtaskDrag(event, false, !!event?.canceled);
          }}
        >
          <NestDepoContext.Provider value={depo}>
          <NestDropContext.Provider value={nest}>
            <div className="ml-2 pl-2 border-l border-violet-400/25">
              <div className="hud-text text-violet-300/80 flex items-center gap-1 mb-1">
                <CornerDownRight className="h-3 w-3" /> İÇ GÖREVLER
              </div>
              <SubtaskTree nodes={sub.children} parentId={sub.id} depth={0} ctx={ctx} />
            </div>
          </NestDropContext.Provider>
          </NestDepoContext.Provider>
        </DragDropProvider>
      )}
      {menu && menuSub && (
        <SubtaskMenu
          x={menu.x}
          y={menu.y}
          sub={menuSub}
          displayNumber={numbers[menu.id]}
          detached={!!detachedSubIds?.has(menu.id)}
          canDetach
          onClose={() => setMenu(null)}
          onAction={(action) => {
            // AŞAMA 10 (kural 8) — ana listedeki menüyle AYNI fonksiyonlar.
            if (action === "detach") {
              if (detachedSubIds?.has(menu.id)) onDockSubtask?.(menu.id);
              else onDetachSubtask?.(menu.id, null);
            } else if (action === "done") {
              onSetSubtasks(updateSubById(all, menu.id, { done: true, status: "done" }), {});
            } else if (action === "pending") {
              onSetSubtasks(updateSubById(all, menu.id, { done: false, status: "pending" }), {});
            } else if (action === "delete") {
              onSetSubtasks(removeSubById(all, menu.id), {});
            }
            setMenu(null);
          }}
        />
      )}
    </div>
  );
};

export default DetachedSubtaskContent;
